import React, { useCallback, useEffect, useState } from "react";
import {
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  StopOutlined
} from "@ant-design/icons";
import { Alert, Button, Modal, Spin, Tooltip, message } from "antd";
import ClientDiaryForm from "./client-diary-form";
import {
  approveHealthClientDiary,
  deleteHealthClientDiary,
  exportHealthClientDiaryCda,
  fetchHealthClientDiary
} from "./health-clients-api";
import { formatDate, formatTime, pick, toNumber } from "./health-clients-utils";
import "./client-diary.css";

const normalizeRow = (row) => {
  const idClient = toNumber(pick(row, "idClient", "IdClient"));
  const idStr = toNumber(pick(row, "idStr", "IdStr"));
  const idSotrUtverdil = toNumber(pick(row, "idSotrUtverdil", "IdSotrUtverdil")) ?? 0;
  const isApproved =
    Boolean(pick(row, "isApproved", "IsApproved")) || idSotrUtverdil > 0;

  return {
    key: `${idClient}-${idStr}`,
    idClient,
    idStr,
    fdate: pick(row, "fdate", "Fdate"),
    text1: pick(row, "text1", "Text1") ?? "",
    text2: pick(row, "text2", "Text2") ?? "",
    sotrName: pick(row, "sotrName", "SotrName") ?? "",
    vidName: pick(row, "vidName", "VidName") ?? "",
    utverdilName: pick(row, "utverdilName", "UtverdilName") ?? "",
    dtStart: pick(row, "dtStart", "DtStart"),
    dtFinish: pick(row, "dtFinish", "DtFinish"),
    isFirst: (toNumber(pick(row, "isFirst", "IsFirst")) ?? 0) === 1,
    isApproved,
    idSotrUtverdil,
    externalId: pick(row, "externalId", "ExternalId") ?? ""
  };
};

const previewText = (value, max = 120) => {
  const s = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const formatTimeRange = (row) => {
  const t1 = formatTime(row.dtStart);
  const t2 = formatTime(row.dtFinish);
  if (!t1 && !t2) return "";
  return `${t1 || "—"}${t2 ? `–${t2}` : ""}`;
};

const DiaryCard = ({ item, onEdit, onCopy, onApprove, onDelete, onCda, cdaLoading }) => {
  const dateLabel = formatDate(item.fdate) || "—";
  const timeLabel = formatTimeRange(item);
  const text1 = previewText(item.text1);
  const text2 = previewText(item.text2);

  return (
    <li>
      <div
        className={`client-diary__card${item.isApproved ? " client-diary__card--approved" : ""}`}
        role="button"
        tabIndex={0}
        aria-label={`${item.isApproved ? "Просмотр" : "Изменить"}: ${dateLabel}`}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
      >
        <div className="client-diary__content">
          <div className="client-diary__head">
            <div className="client-diary__title-block">
              <h4 className="client-diary__title">
                <span className="client-diary__date">{dateLabel}</span>
                {timeLabel ? <span className="client-diary__time">{timeLabel}</span> : null}
                {item.vidName ? <span className="client-diary__badge">{item.vidName}</span> : null}
                {item.isFirst ? <span className="client-diary__badge">Первичный</span> : null}
                {item.isApproved ? (
                  <span className="client-diary__badge client-diary__badge--ok">Утверждено</span>
                ) : null}
                {item.externalId ? (
                  <span className="client-diary__badge client-diary__badge--cda">CDA</span>
                ) : null}
              </h4>
            </div>
            <div className="client-diary__head-right" onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Копировать">
                <Button
                  type="text"
                  size="small"
                  className="client-diary__action"
                  icon={<CopyOutlined />}
                  onClick={onCopy}
                />
              </Tooltip>
              <Tooltip
                title={
                  !item.isApproved
                    ? "Сначала утвердите запись"
                    : item.externalId
                      ? "CDA уже создан"
                      : "Сформировать CDA"
                }
              >
                <Button
                  type="text"
                  size="small"
                  className="client-diary__action"
                  icon={<FileTextOutlined />}
                  loading={cdaLoading}
                  disabled={!item.isApproved || Boolean(item.externalId)}
                  onClick={onCda}
                />
              </Tooltip>
              <Tooltip title={item.isApproved ? "Снять утверждение" : "Утвердить"}>
                <Button
                  type="text"
                  size="small"
                  className="client-diary__action"
                  icon={item.isApproved ? <StopOutlined /> : <CheckOutlined />}
                  onClick={onApprove}
                />
              </Tooltip>
              <Tooltip title="Удалить">
                <Button
                  type="text"
                  size="small"
                  danger
                  className="client-diary__action"
                  disabled={item.isApproved}
                  icon={<DeleteOutlined />}
                  onClick={onDelete}
                />
              </Tooltip>
            </div>
          </div>

          {(item.sotrName || (item.isApproved && item.utverdilName)) && (
            <div className="client-diary__people">
              {item.sotrName ? <span>{item.sotrName}</span> : null}
              {item.isApproved && item.utverdilName ? <span>утв. {item.utverdilName}</span> : null}
            </div>
          )}

          {text1 ? (
            <div className="client-diary__block">
              <span className="client-diary__label">Общие сведения</span>
              <span className="client-diary__text">{text1}</span>
            </div>
          ) : null}
          {text2 ? (
            <div className="client-diary__block">
              <span className="client-diary__label">Протокол</span>
              <span className="client-diary__text">{text2}</span>
            </div>
          ) : null}
          {!text1 && !text2 ? <div className="client-diary__empty-text">Нет текста</div> : null}
        </div>
      </div>
    </li>
  );
};

/**
 * Вкладка «Дневник»: карточки записей + редактор.
 */
const ClientDiary = ({ idClient, open = true }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editIdStr, setEditIdStr] = useState(null);
  const [copyFromIdStr, setCopyFromIdStr] = useState(null);
  const [cdaLoadingId, setCdaLoadingId] = useState(null);

  const load = useCallback(() => {
    if (!idClient || open === false) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchHealthClientDiary(idClient)
      .then((data) => {
        if (cancelled) return;
        setRows((Array.isArray(data) ? data : []).map(normalizeRow).filter((r) => r.idStr != null));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Не удалось загрузить дневник");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idClient, open]);

  useEffect(() => load(), [load]);

  const openAdd = () => {
    setEditIdStr(null);
    setCopyFromIdStr(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditIdStr(row.idStr);
    setCopyFromIdStr(null);
    setFormOpen(true);
  };

  const openCopy = (row) => {
    setEditIdStr(null);
    setCopyFromIdStr(row.idStr);
    setFormOpen(true);
  };

  const handleDelete = (row) => {
    if (row.isApproved) {
      message.warning("Утверждённую запись нельзя удалить");
      return;
    }
    Modal.confirm({
      title: "Удалить запись дневника?",
      content: `Запись №${row.idStr} от ${formatDate(row.fdate) || "—"}`,
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await deleteHealthClientDiary(idClient, row.idStr);
          message.success("Запись удалена");
          load();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          return Promise.reject(e);
        }
      }
    });
  };

  const handleCda = (row) => {
    Modal.confirm({
      title: "Сформировать CDA?",
      content: `Запись №${row.idStr}. XML будет сохранён в реестре ЕГИСЗ и скачан.`,
      okText: "Сформировать",
      cancelText: "Отмена",
      onOk: async () => {
        setCdaLoadingId(row.idStr);
        try {
          const { blob, fileName } = await exportHealthClientDiaryCda(idClient, row.idStr);
          const href = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = href;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(href);
          message.success("CDA сформирован");
          load();
        } catch (e) {
          message.error(e.message || "Не удалось сформировать CDA");
          return Promise.reject(e);
        } finally {
          setCdaLoadingId(null);
        }
      }
    });
  };

  const handleApprove = (row) => {
    const approve = !row.isApproved;
    Modal.confirm({
      title: approve ? "Утвердить запись?" : "Снять утверждение?",
      okText: approve ? "Утвердить" : "Снять",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await approveHealthClientDiary(idClient, row.idStr, approve);
          message.success(approve ? "Запись утверждена" : "Утверждение снято");
          load();
        } catch (e) {
          message.error(e.message || "Операция не выполнена");
          return Promise.reject(e);
        }
      }
    });
  };

  if (!idClient) {
    return <div className="client-tab client-tab--placeholder">Выберите клиента</div>;
  }

  return (
    <div className="client-tab client-diary">
      <div className="client-diary__toolbar">
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          Добавить
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button onClick={load}>Повторить</Button>}
          className="client-diary__error"
        />
      ) : null}

      <div className="client-diary__body">
        {loading && !rows.length ? (
          <div className="client__center">
            <Spin tip="Загрузка..." />
          </div>
        ) : rows.length ? (
          <ul className="client-diary__list">
            {rows.map((row) => (
              <DiaryCard
                key={row.key}
                item={row}
                onEdit={() => openEdit(row)}
                onCopy={() => openCopy(row)}
                onApprove={() => handleApprove(row)}
                onDelete={() => handleDelete(row)}
                onCda={() => handleCda(row)}
                cdaLoading={cdaLoadingId === row.idStr}
              />
            ))}
          </ul>
        ) : (
          <div className="client__center">Нет записей дневника</div>
        )}
      </div>

      <ClientDiaryForm
        idClient={idClient}
        idStr={editIdStr}
        copyFromIdStr={copyFromIdStr}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setCopyFromIdStr(null);
        }}
        onSaved={load}
      />
    </div>
  );
};

export default ClientDiary;
